import React, { useEffect, useMemo, useState } from "react"
import { Dropdown, Form } from "react-bootstrap";

export const SelectFilter = ({handleFilterChange, selector, filter, data, defaultValue}) => {

  const [resolvedOptions, setResolvedOptions] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // `selector` (filter.options) may be:
  //  - a function returning an array or a Promise of an array (e.g. a cached request fn)
  //  - an array of options
  //  - undefined => derive distinct values from the loaded page data
  useEffect(() => {
    let active = true;
    if (typeof selector === "function") {
      Promise.resolve(selector()).then((opts) => {
        if (active) setResolvedOptions(opts ?? []);
      });
    } else if (Array.isArray(selector)) {
      setResolvedOptions(selector);
    }
    return () => { active = false; };
  }, [selector]);

  const options = useMemo(() => {
    if (selector) return resolvedOptions;
    const list = Array.from([...new Set(data?.map(d => d[filter.fieldName]))]).sort((a,b) => {
      if(a?.toString().trim() < b?.toString().trim()) return -1;
      if(a?.toString().trim() > b?.toString().trim()) return 1;
      return 0;
    });
    return list;
  }, [data, resolvedOptions, selector]);

  // Searchable variant for long option lists: a dropdown whose menu has a
  // filter box at the top and a scrollable, substring-filtered option list.
  // The filter box owns keyboard nav (arrows move the highlight, Enter selects)
  // since it captures keydown before the Dropdown's built-in handler.
  if (filter.searchable) {
    const filtered = options.filter((o) => String(o).toLowerCase().includes(search.toLowerCase()));
    // Index 0 is the clear (-- label --) row; the rest are the filtered options.
    const items = ["", ...filtered.map((o) => String(o))];
    const select = (val: string) => {
      handleFilterChange(val, filter.fieldName);
      setShow(false);
    };
    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlight >= 0 && highlight < items.length) select(items[highlight]);
      } else if (e.key === "Escape") {
        setShow(false);
      }
      // Stop the Dropdown's own key handler from also acting on this event.
      e.stopPropagation();
    };
    return (
      <Form.Group className="me-3 mb-1">
        <Dropdown
          show={show}
          onToggle={(next) => { setShow(next); if (next) { setSearch(""); setHighlight(0); } }}
        >
          <Dropdown.Toggle
            size="sm"
            variant="outline-secondary"
            className="text-start d-flex align-items-center justify-content-between"
            style={{ minWidth: 180, background: "#fff", color: "#212529" }}
          >
            <span className="text-truncate">{defaultValue ? String(defaultValue) : `-- ${filter.label} --`}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ minWidth: 220, maxHeight: 300, overflowY: "auto" }}>
            <div className="px-2 pb-1" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <Form.Control
                autoFocus
                size="sm"
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlight(0); }}
                onKeyDown={onKeyDown}
              />
            </div>
            {items.map((val, index) => (
              <Dropdown.Item
                key={val || "__clear__"}
                active={index === highlight}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => select(val)}
                ref={index === highlight ? (el: HTMLElement | null) => el?.scrollIntoView({ block: "nearest" }) : undefined}
              >
                {val === "" ? `-- ${filter.label} --` : val}
              </Dropdown.Item>
            ))}
            {filtered.length === 0 && <div className="px-2 py-1 text-muted small">No matches</div>}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>
    );
  }

  return (
    <Form.Group className="me-3 mb-1">
      {/* Controlled (not defaultValue): async options mount after first render,
          so an uncontrolled select can't reflect the selected value. */}
      <Form.Select size="sm" value={defaultValue ?? ''} onChange={(e) => { handleFilterChange(e.target.value, filter.fieldName); }}>
        <option value="">-- {filter.label} --</option>
        {options.map((type, i) => (
          <option value={type} key={type}>{type}</option>
        ))}
      </Form.Select>
    </Form.Group>
  )
}